import React, { useMemo } from 'react';
import { observer } from 'mobx-react';

import classNames from 'classnames';
import { isBoolean } from 'lodash';

import { Button } from 'semantic-ui-react';

import '../css/StudentAssignmentPrimaryActionButton.less';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import { ASSIGNMENT_STATUS } from '../managers/AssignmentManager';
import { CONTENT_ITEM_TYPES, CONTENT_MODE } from '../managers/ContentManager';
import studentAssignmentPlayerLauncherManager from '../managers/StudentAssignmentPlayerLauncherManager';

import PopupService from '../services/PopupService';
import StudentAssignmentPlayerLauncherService from '../services/StudentAssignmentPlayerLauncherService';
import StudentAssignmentService from '../services/StudentAssignmentService';

const StudentAssignmentPrimaryActionButton = observer((props = {}) => {
  const {
    assignment,
    disabled,
    icon,
    iconDisabled,
    onAfterDownloadAssignment,
    onLaunchAssignment,
    popupOptions = {},
    t
  } = props;

  // cache via useMemo to avoid re-mounting on every render
  const StudentAssignmentLockedMessage = useMemo(() => SatCoreComponent('StudentAssignmentLockedMessage'), []);

  if (!assignment) {
    return null;
  }

  const buttonConfig = StudentAssignmentService.getStudentAssignmentPrimaryActionButtonConfig(assignment);
  if (buttonConfig.hidden) {
    return null;
  }
  const isDisabled = isBoolean(disabled)
    ? disabled
    : (buttonConfig.disabled || studentAssignmentPlayerLauncherManager.isLaunchingAssignment);

  const handleClick = () => {
    if (isDisabled) {
      return;
    }
    if (buttonConfig.actionKey === 'download') {
      handleDownloadAssignment();
      return;
    }
    if (buttonConfig.actionKey === 'launch') {
      handleLaunchAssignment();
    }
  };

  const handleDownloadAssignment = () => {
    window.open(CONTENT_ITEM_TYPES.getFileDownloadUrl(assignment.contentItemId));
    if (typeof onAfterDownloadAssignment === 'function') {
      onAfterDownloadAssignment();
    }
  };

  const handleLaunchAssignment = () => {
    const isReviewStatus = assignment.status === ASSIGNMENT_STATUS.CLOSED
      || assignment.status === ASSIGNMENT_STATUS.COMPLETED;
    // turnInLate is a late attempt on a closed assignment → (ASSESS, not REVIEW).
    const isTurnInLate = buttonConfig.labelKey === 'turnInLate';
    const contentMode = (isReviewStatus && !isTurnInLate) ? CONTENT_MODE.REVIEW : CONTENT_MODE.ASSESS;
    if (typeof onLaunchAssignment === 'function') {
      onLaunchAssignment({ assignment, contentMode, turnInLate: isTurnInLate });
      return;
    }
    StudentAssignmentPlayerLauncherService.launchAssignment({ assignment, contentMode, turnInLate: isTurnInLate });
  };

  const renderStudentAssignmentPrimaryActionButton = () => {
    const button = renderButton();
    if (shouldRenderInPopup()) {
      return renderButtonInPopup(button);
    }
    return button;
  };

  const shouldRenderInPopup = () => {
    return !!buttonConfig.lockedMessageKey || !!popupOptions.content;
  };

  const renderButton = () => {
    const effectiveIcon = (isDisabled && iconDisabled) ? iconDisabled : icon;
    return (
      <Button
        className={classNames(
          'StudentAssignmentPrimaryActionButton',
          (!!buttonConfig.actionKey && `StudentAssignmentPrimaryActionButton-${buttonConfig.actionKey}`),
          buttonConfig.className
        )}
        disabled={isDisabled}
        onClick={handleClick}>
        {!!buttonConfig.labelKey && t(buttonConfig.labelKey)}
        {!!effectiveIcon && (
          <img alt='' className='student-assignment-primary-action-button-icon' src={effectiveIcon} />
        )}
      </Button>
    );
  };

  const renderButtonInPopup = (button) => {
    // popup precedence: caller-provided content via popupOptions wins;
    // falls back to the internal locked-message popup.
    const lockedMessage = buttonConfig.lockedMessageKey ? (
      <StudentAssignmentLockedMessage
        assignment={assignment}
        lockedMessageKey={buttonConfig.lockedMessageKey} />
    ) : null;
    return PopupService.renderPopup({
      className: 'custom-popup student-assignment-locked-message-popup',
      content: lockedMessage,
      on: 'hover',
      position: 'bottom center',
      wide: false,
      ...popupOptions,
      // eslint-disable-next-line sort-keys
      trigger: button
    });
  };

  return renderStudentAssignmentPrimaryActionButton();
});

export default StudentAssignmentPrimaryActionButton;

SatCoreRegister('StudentAssignmentPrimaryActionButton', StudentAssignmentPrimaryActionButton);
